import {produce} from "immer";

import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as search from "@client/features/search";

import * as router from "@client/router";

import {calculateMealMetrics, getMetrics} from "@client/calculations";
import * as forms from "@client/site/forms";
import {AppDispatch} from "@client/store";
import {server} from "@reactivated";

export const useRecipeForm = ({
    initial,
    disabled,
}: {
    initial?: Partial<
        forms.PFormHandler<server.journal.nutrition.CreateRecipe>["values"]
    > | null;
    disabled?: true;
}) => {
    const form = forms.useAutoPform(
        server.journal.nutrition.CreateRecipe,
        initial ?? null,
    );

    const layout = forms.usePformLayout({
        form,
        disabled,
        layout: [
            {name: "name"},
            {
                label: "Recipe units",
                fields: [{name: "container_servings"}, {name: "serving_size_unit"}],
            },
            {
                label: "Recipe weight",
                fields: [{name: "container_quantity"}, {name: "container_unit"}],
            },
            {name: "notes"},
        ],
    });

    return {form, layout};
};

export const confirmAndDeleteRecipe =
    (recipe: server.journal.nutrition.Recipe.output) =>
    async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );

        if (confirmed) {
            await server.journal.nutrition.delete_recipe({recipe_id: recipe.id});
            void dispatch(search.loadSearch({reset: true}));
            router.push("recipe_list.index", {});
            dispatch(notify(`Your recipe was deleted`));
        }
    };

export function addEntriesToRecipe({
    recipe,
    entries,
}: {
    recipe: server.journal.nutrition.Recipe.output;
    entries: server.journal.nutrition.ExpandedEntry.output[];
}) {
    const createdEntries = entries.map((entry) => ({
        ...entry,
        expanded_recipe: entry.expanded_recipe.map((ingredient) => ({
            ...ingredient,
            uuid: crypto.randomUUID(),
        })),
        uuid: crypto.randomUUID(),
    }));

    const updatedRecipe = produce(recipe, (draftState) => {
        draftState.expanded_recipe = draftState.expanded_recipe.concat(createdEntries);

        const recipeCalculations = calculateMealMetrics({
            entries: draftState.expanded_recipe,
        });

        if (draftState.expanded_recipe.length > 0) {
            for (const [metric, metricPg] of getMetrics({useKilojoules: false})
                .ALL_METRICS) {
                draftState[metricPg] =
                    recipeCalculations[metric]
                        ?.dividedBy(draftState.container_servings)
                        .toString() ?? null;
            }
        }
    });

    void server.journal.nutrition.update_recipe({
        recipe_uuid: recipe.uuid,
        entries: updatedRecipe.expanded_recipe,
    });

    return updatedRecipe;
}
